import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RecommendationFilterState } from '@/types/recommendation';

const DEFAULT_FILTER: RecommendationFilterState = {
  searchQuery: '',
  roleFilter: null,
  locationFilter: null,
  minMatchScore: 0,
  departmentFilter: null,
};

interface RecommendationFilterPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: RecommendationFilterState;
  onFilterChange: (filter: RecommendationFilterState) => void;
  availableRoles: string[];
  availableLocations: string[];
  availableDepartments: string[];
  trigger: React.ReactNode;
}

export function RecommendationFilterPopover({
  open,
  onOpenChange,
  filter,
  onFilterChange,
  availableRoles,
  availableLocations,
  availableDepartments,
  trigger,
}: RecommendationFilterPopoverProps) {
  const handleReset = () => {
    onFilterChange({ ...DEFAULT_FILTER, searchQuery: filter.searchQuery });
  };

  const handleApply = () => {
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-3" align="end">
        {/* Role */}
        <div className="space-y-1">
          <Label className="text-xs">Role</Label>
          <Select
            value={filter.roleFilter || '__any__'}
            onValueChange={v =>
              onFilterChange({ ...filter, roleFilter: v === '__any__' ? null : v })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Any role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any role</SelectItem>
              {availableRoles.map(r => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div className="space-y-1">
          <Label className="text-xs">Location</Label>
          <Select
            value={filter.locationFilter || '__any__'}
            onValueChange={v =>
              onFilterChange({ ...filter, locationFilter: v === '__any__' ? null : v })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Any location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any location</SelectItem>
              {availableLocations.map(l => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Department */}
        <div className="space-y-1">
          <Label className="text-xs">Department</Label>
          <Select
            value={filter.departmentFilter || '__any__'}
            onValueChange={v =>
              onFilterChange({ ...filter, departmentFilter: v === '__any__' ? null : v })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Any department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any department</SelectItem>
              {availableDepartments.map(d => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Minimum Match Score */}
        <div className="space-y-2">
          <Label className="text-xs">
            Minimum match score:{' '}
            <span className="font-medium">At least {filter.minMatchScore}%</span>
          </Label>
          <Slider
            min={0}
            max={100}
            step={5}
            value={[filter.minMatchScore]}
            onValueChange={([v]) => onFilterChange({ ...filter, minMatchScore: v })}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-between pt-2">
          <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
            Reset
          </Button>
          <Button type="button" variant="default" size="sm" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
